// Media type detection utility

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv', '.mpeg', '.mpg', '.3gpp'];
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.aac', '.flac', '.opus', '.ogg', '.wma'];
const PDF_EXTENSIONS = ['.pdf'];
const MARKDOWN_EXTENSIONS = ['.md', '.markdown'];
const HTML_EXTENSIONS = ['.html', '.htm'];
const CSV_EXTENSIONS = ['.csv', '.tsv'];
const DOCUMENT_EXTENSIONS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
// Oct 8 2026: .fbx (Autodesk; our 3D outputs ship an FBX copy) and .obj fell into 'code' and showed
// "appears to be binary". ThreeDViewer loads both with the loaders three.js already ships.
const THREE_D_EXTENSIONS = ['.glb', '.gltf', '.stl', '.fbx', '.obj'];
// Oct 29 2026: MIDI is note data, not sound. Python's mimetypes calls .mid 'audio/midi', so every 'audio/*' check sent it
// to the <audio> player, which no browser can decode ("Unable to play audio"). MIDI gets the plain download treatment.
const MIDI_EXTENSIONS = ['.mid', '.midi', '.kar'];
const MIDI_MIME_TYPES = ['audio/midi', 'audio/mid', 'audio/x-midi', 'audio/x-mid', 'audio/sp-midi'];

/** True for a MIDI file, by mime type or by name (a signed URL with a query string is fine). Shared with FileTile. */
export const isMidiFile = (mimeType, name) => {
  const mime = (mimeType || '').toLowerCase().split(';')[0].trim();
  if (MIDI_MIME_TYPES.includes(mime)) return true;
  const n = (name || '').toLowerCase().split('?')[0].split('#')[0].replace(/[.\s]+$/, '');
  return MIDI_EXTENSIONS.some(ext => n.endsWith(ext));
};

// Binary/non-text extensions that should NOT be treated as code
const BINARY_EXTENSIONS = [
  '.exe', '.dll', '.so', '.dylib', '.bin', '.dat', '.db', '.sqlite',
  '.zip', '.tar', '.gz', '.rar', '.7z', '.bz2',
  '.class', '.jar', '.war', '.ear',
  '.pyc', '.pyo', '.pyd',
  '.glb', '.gltf', '.stl', '.fbx',
  // Oct 13 2026: .blend (Blender's saved session) has no browser loader; it fell into 'code' and showed
  // "appears to be binary". Listed here it gets the plain download treatment.
  '.blend',
  // Oct 29 2026: MIDI (see isMidiFile)
  ...MIDI_EXTENSIONS,
  '.o', '.a', '.lib',
  '.ttf', '.otf', '.woff', '.woff2', '.eot',
  '.ico', '.icns'
];

const detectFromURL = (url) => {
  if (!url) return 'unknown';

  // Strip query params and hash before checking extensions
  // Signed URLs (GCS, S3) have params like ?GoogleAccessId=...&Expires=...
  // Sep 20 2026: strip trailing dots/spaces too. "cloud_ref2.png." (a Windows path copied with the sentence's
  // period; Windows opened it, so the upload went through) has no recognisable extension and fell into 'code',
  // so the viewer fetched a PNG as text ("appears to be binary"). Older rows keep such names; this covers them.
  const urlLower = url.toLowerCase().split('?')[0].split('#')[0].replace(/[.\s]+$/, '');

  // Check specific type extensions first
  if (IMAGE_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'image';
  if (VIDEO_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'video';
  if (AUDIO_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'audio';
  if (PDF_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'pdf';
  if (MARKDOWN_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'markdown';
  if (HTML_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'html';
  if (CSV_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'csv';
  if (DOCUMENT_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'document';
  if (THREE_D_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'threed';

  // Check if it's a known binary extension
  if (BINARY_EXTENSIONS.some(ext => urlLower.endsWith(ext))) return 'unknown';

  // Check if it has any file extension at all
  const lastDot = urlLower.lastIndexOf('.');
  const lastSlash = urlLower.lastIndexOf('/');
  
  // Has extension and it's not a URL parameter
  if (lastDot > lastSlash && lastDot !== -1) {
    // Default to 'code' for any text-based file with extension
    // This catches: .rs, .toml, .go, .kt, .swift, .rb, .php, .sh, .bash, .sql, etc.
    return 'code';
  }

  // No extension or unclear - return unknown
  return 'unknown';
};

const detectFromMimeType = (mimeType) => {
  if (!mimeType) return null;

  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return isMidiFile(mimeType) ? 'unknown' : 'audio';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'text/markdown' || mimeType === 'text/x-markdown') return 'markdown';
  if (mimeType === 'text/html' || mimeType === 'application/xhtml+xml') return 'html';
  if (mimeType === 'text/csv' || mimeType === 'text/tab-separated-values') return 'csv';
  if (mimeType === 'model/gltf-binary' || mimeType === 'model/gltf+json' || mimeType === 'model/stl' || mimeType === 'application/sla') return 'threed';
  if (mimeType.includes('officedocument') || mimeType.includes('msword') ||
    mimeType.includes('ms-excel') || mimeType.includes('ms-powerpoint')) {
    return 'document';
  }
  
  // Any text/* MIME type should be treated as code
  if (mimeType.startsWith('text/') || mimeType === 'application/json') return 'code';

  return null;
};

export const detectMediaType = (media) => {
  // Handle string URLs (backward compatibility)
  if (typeof media === 'string') {
    return detectFromURL(media);
  }

  // Handle media objects from posts
  const { file_type, mimeType, mime_type, url, filename, original_filename } = media || {};

  // MIDI before file_type: callers tag every 'audio/*' file as file_type 'audio' (see isMidiFile)
  if (isMidiFile(mimeType || mime_type, '') || [url, filename, original_filename].some(n => isMidiFile(null, n))) {
    return 'unknown';
  }

  // Use file_type if available (posts system)
  if (file_type) {
    if (file_type === 'image') return 'image';
    if (file_type === 'video') return 'video';
    if (file_type === 'audio') return 'audio';
  }

  // Check MIME type (most reliable)
  const mType = mimeType || mime_type;
  const mimeDetected = detectFromMimeType(mType);
  if (mimeDetected) {
    return mimeDetected;
  }

  // Fallback to URL/filename detection
  const fileUrl = url || filename || original_filename || '';
  return detectFromURL(fileUrl);
};

// Normalize media files to consistent format
export const normalizeMediaFiles = (mediaFiles) => {
  if (!mediaFiles || !Array.isArray(mediaFiles)) return [];

  return mediaFiles.map((media, index) => {
    if (typeof media === 'object' && media !== null) {
      const normalized = {
        ...media,
        id: media.id || `media-${index}`,
        url: media.url || media.signed_url || media.src,
        type: detectMediaType(media)
      };
      return normalized;
    }

    if (typeof media === 'string') {
      return {
        id: `media-${index}`,
        url: media,
        type: detectFromURL(media),
        original_filename: media.split('/').pop()
      };
    }

    return null;
  }).filter(Boolean);
};

// Get media URL from various formats
export const getMediaUrl = (media) => {
  if (typeof media === 'string') return media;
  return media.url || media.signed_url || media.src || '';
};

// Get media title/name
export const getMediaTitle = (media) => {
  if (typeof media === 'string') {
    const parts = media.split('/');
    return parts[parts.length - 1];
  }
  return media.title || media.original_filename || media.filename || 'Untitled';
};

// Get file size
export const getMediaFileSize = (media) => {
  if (typeof media === 'object' && media !== null) {
    return media.file_size || media.size || null;
  }
  return null;
};

// Get file icon based on type
export const getFileIcon = (type) => {
  switch (type) {
    case 'image': return '🖼️';
    case 'video': return '🎬';
    case 'audio': return '🎵';
    case 'pdf': return '📄';
    case 'markdown': return '📝';
    case 'html': return '🌐';
    case 'csv': return '📊';
    case 'document': return '📄';
    case 'threed': return '🧊';
    case 'code': return '💻';
    default: return '📎';
  }
};
